import React, { useMemo, useState } from 'react';
import { Formik, Form } from 'formik';
import ReactDatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import ShowModal from '../../../utils/ShowModal';
import Dropdown from '../../inputFields/DropDown';
import { B2B_INTERACTION_TYPE_OPTIONS, B2B_LOG_OUTCOME_OPTIONS, B2B_PURPOSE_OPTIONS } from '../../../constants/b2bWorkflow';
import { logB2BInteraction } from '../../../services/b2bLeads';

const parseDurationToSeconds = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw) return undefined;
    if (/^\d+$/.test(raw)) return Number(raw);
    const minutes = raw.match(/(\d+)\s*m/);
    const seconds = raw.match(/(\d+)\s*s/);
    const total = (minutes ? Number(minutes[1]) * 60 : 0) + (seconds ? Number(seconds[1]) : 0);
    return total || undefined;
};

const B2BLogInteractionModal = ({ isOpen, onClose, lead, completeFollowUpId = null, onSuccess }) => {
    const [apiError, setApiError] = useState('');
    const leadTitle = useMemo(() => lead?.shop_name || lead?.owner_name || 'Lead', [lead]);

    const handleSubmit = async (values, { setSubmitting }) => {
        setApiError('');
        const payload = {
            lead: lead?.id || lead?.lead_id,
            interaction_type: values.interaction_type,
            outcome: values.outcome,
            note: values.note || undefined,
        };



        if (completeFollowUpId) {
            payload.complete_follow_up = completeFollowUpId;
        }

        if (values.schedule_next && values.follow_up_at) {
            payload.next_follow_up = {
                follow_up_at: values.follow_up_at.toISOString(),
                purpose: values.purpose,
                remarks: values.remarks || '',
            };
        }

        try {
            const res = await logB2BInteraction(payload);
            if (res?.status === 200 || res?.status === 201) {
                toast.success('Interaction saved');
                onSuccess?.(res.data);
                onClose?.();
            } else {
                const message = res?.data?.detail || res?.data?.error || 'Failed to save interaction';
                setApiError(message);
            }
        } catch (error) {
            setApiError('Error connecting to server');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ShowModal
            isOpen={isOpen}
            onRequestClose={onClose}
            title={`Log Interaction: ${leadTitle}`}
            modalWidth="520px"
            componentToPass={
                <Formik
                    initialValues={{
                        interaction_type: 'call',
                        call_direction: 'outgoing',
                        duration: '',
                        outcome: '',
                        note: '',
                        schedule_next: false,
                        follow_up_at: null,
                        purpose: 'call',
                        remarks: '',
                    }}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue, isSubmitting }) => (
                        <Form className="space-y-4">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Type</label>
                                <div className="grid grid-cols-3 bg-gray-100 p-1 rounded-lg">
                                    {B2B_INTERACTION_TYPE_OPTIONS.map(option => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onClick={() => setFieldValue('interaction_type', option.value)}
                                            className={`text-[11px] font-bold py-2 rounded-md transition-all ${values.interaction_type === option.value ? 'bg-white text-primary shadow-sm' : 'text-gray-500'}`}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </div>



                            <Dropdown
                                label="Outcome"
                                options={B2B_LOG_OUTCOME_OPTIONS}
                                value={values.outcome}
                                onChange={(val) => setFieldValue('outcome', val)}
                                placeholder="Select outcome"
                                needSearch
                                compact
                            />

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                                <textarea
                                    value={values.note}
                                    onChange={(e) => setFieldValue('note', e.target.value)}
                                    rows={3}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                                    placeholder="Conversation summary..."
                                />
                            </div>

                            <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={values.schedule_next}
                                    onChange={(e) => setFieldValue('schedule_next', e.target.checked)}
                                    className="accent-primary"
                                />
                                Schedule next follow-up
                            </label>

                            {values.schedule_next && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-emerald-50/50 border border-emerald-100 rounded-xl p-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Date & time</label>
                                        <ReactDatePicker
                                            selected={values.follow_up_at}
                                            onChange={(date) => setFieldValue('follow_up_at', date)}
                                            showTimeSelect
                                            timeIntervals={15}
                                            dateFormat="yyyy-MM-dd h:mm aa"
                                            placeholderText="Select date and time"
                                            portalId="root"
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                                        />
                                    </div>
                                    <Dropdown
                                        label="Purpose"
                                        options={B2B_PURPOSE_OPTIONS}
                                        value={values.purpose}
                                        onChange={(val) => setFieldValue('purpose', val)}
                                        compact
                                    />
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                                        <input
                                            value={values.remarks}
                                            onChange={(e) => setFieldValue('remarks', e.target.value)}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                                            placeholder="Follow-up context"
                                        />
                                    </div>
                                </div>
                            )}

                            {apiError && (
                                <div className="bg-rose-50 text-rose-700 border border-rose-100 rounded-lg px-3 py-2 text-xs font-semibold">
                                    {typeof apiError === 'string' ? apiError : JSON.stringify(apiError)}
                                </div>
                            )}

                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-bold text-gray-600 border border-gray-200 rounded-lg">
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting || !values.outcome || (values.schedule_next && !values.follow_up_at)}
                                    className="px-5 py-2 text-xs font-black bg-primary text-white rounded-lg disabled:bg-gray-300"
                                >
                                    {isSubmitting ? 'Saving...' : 'Save Interaction'}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            }
        />
    );
};

export default B2BLogInteractionModal;
